import booleanClockwise from '@turf/boolean-clockwise';
import { polygon, lineString } from '@turf/helpers';
import union from '@turf/union';
import isEmpty from 'lodash/isEmpty';

import {
    /* 
    buildMonoPitchedRoofAreas,
    buildPitchedRoofAreas, */
    computeOffsetPoint,
    getCentralLine,
    getRefPoints,
} from 'common/components/segment/asceHelpers';
import {
    POLYGON_SEGMENT,
    POLYGON_RECTANGLE,
    POLYGON_CIRCLE,
} from 'common/components/segment/constants';
import { getPolygonPointsWithSetback } from 'common/components/segment/solarGeometryhelpers';

import {
    SEGMENT_COLOR,
    SEGMENT_STROKE_COLOR,
    SEGMENT_STROKE_ACTIVE_COLOR,
} from '../constants';
import {
    addPathListeners,
    createMeterLabelPopup,
    createDiameterLabelPopup,
} from '../helpers';

import SolarModulePolygonBuilder from './SolarModulePolygonBuilder';

const getAzimuth = ({ polygon, from, to, google }) => {
    const heading = google.maps.geometry.spherical.computeHeading(from, to);
    const interpolated = google.maps.geometry.spherical.interpolate(
        from,
        to,
        0.5,
    );

    let newPosition = google.maps.geometry.spherical.computeOffset(
        interpolated,
        0.02,
        heading + 90,
    );

    if (google.maps.geometry.poly.containsLocation(newPosition, polygon)) {
        newPosition = google.maps.geometry.spherical.computeOffset(
            interpolated,
            0.02,
            heading - 90,
        );
    }

    let initAzimuth = google.maps.geometry.spherical.computeHeading(
        interpolated,
        newPosition,
    );

    if (initAzimuth < 0) initAzimuth += 360;

    return initAzimuth.toFixed(2);
};

const getPolygonPaths = (polygon) => {
    let _paths = [];
    let paths = polygon.getPath();
    paths.forEach(function (latlng) {
        _paths.push({ lat: latlng.lat(), lng: latlng.lng() });
    });

    return _paths;
};

const buildContextSegments = ({
    google,
    map,
    metadata,
    onContextMenu,
    segmentId,
    shapeCoords,
}) => {
    if (!Array.isArray(shapeCoords)) return;
    let segments = new google.maps.MVCArray();
    if (!onContextMenu) return null;
    const polygon = new google.maps.Polygon({ paths: shapeCoords });
    const paths = polygon.getPaths();
    const options = {
        map,
        strokeOpacity: 0,
        strokeWeight: 13,
        zIndex: 100,
    };
    let azimuths = [];

    paths.forEach((path) => {
        const coordinates = path.getArray();

        segments.push(
            new google.maps.Polyline({
                ...options,
                path: [coordinates[coordinates.length - 1], coordinates[0]],
            }),
        );

        azimuths.push(
            getAzimuth({
                polygon,
                from: coordinates[coordinates.length - 1],
                to: coordinates[0],
                google,
            }),
        );

        for (let i = 0; i < coordinates.length - 1; i++) {
            segments.push(
                new google.maps.Polyline({
                    ...options,
                    path: [coordinates[i], coordinates[i + 1]],
                }),
            );

            azimuths.push(
                getAzimuth({
                    polygon,
                    from: coordinates[i],
                    to: coordinates[i + 1],
                    google,
                }),
            );
        }
    });

    segments.forEach((segment, index) => {
        google.maps.event.addListener(segment, 'contextmenu', (e) => {
            onContextMenu({
                azimuth: azimuths[index],
                event: e.domEvent,
                metadata,
                segment,
                segmentId,
            });
        });
    });

    return segments;
};

const getPartialShadows = ({ polygonPaths, shadowPaths }) => {
    let partialShadows = [];
    for (let i = 0; i < polygonPaths.length - 1; i++) {
        partialShadows.push([
            polygonPaths[i],
            shadowPaths[i],
            shadowPaths[i + 1],
            polygonPaths[i + 1],
        ]);
    }

    partialShadows.push([
        polygonPaths[polygonPaths.length - 1],
        shadowPaths[polygonPaths.length - 1],
        shadowPaths[0],
        polygonPaths[0],
    ]);

    return partialShadows;
};

const getUnionShadow = (partialShadows) => {
    let temp_coords = partialShadows[0].map((point) => [point.lng, point.lat]);
    temp_coords.push(temp_coords[0]);

    for (let i = 1; i < partialShadows.length; i++) {
        let polyunion = null;
        let shadowCoords = partialShadows[i].map((point) => [
            point.lng,
            point.lat,
        ]);
        shadowCoords.push(shadowCoords[0]);

        const poly1 = polygon([temp_coords]);
        const poly2 = polygon([shadowCoords]);

        try {
            polyunion = union(poly1, poly2);
        } catch (e) {
            polyunion = null;
        }

        if (!polyunion) continue;
        temp_coords = polyunion.geometry.coordinates[0];
    }

    return temp_coords;
};

const buildShader = ({ google, height, shapeCoords, sunPosition, zIndex }) => {
    if (
        !Array.isArray(shapeCoords) ||
        !height ||
        height === 0 ||
        typeof height === 'undefined' ||
        sunPosition.elevation <= 0
    )
        return null;
    const polygon = new google.maps.Polygon({ paths: shapeCoords });
    const polygonPaths = getPolygonPaths(polygon);
    const shadowLength =
        height / Math.tan((sunPosition.elevation * Math.PI) / 180);
    const shadowDirection = sunPosition.azimuth - 180;
    let shadowPaths = [];

    polygonPaths.forEach((path) => {
        const newPosition = google.maps.geometry.spherical.computeOffset(
            path,
            shadowLength,
            shadowDirection,
        );
        shadowPaths.push({ lat: newPosition.lat(), lng: newPosition.lng() });
    });

    const partialShadows = getPartialShadows({ polygonPaths, shadowPaths });
    const unionShadows = getUnionShadow(partialShadows);

    const mergedPolygonCoords = unionShadows.map((coord) => ({
        lat: coord[1],
        lng: coord[0],
    }));

    const mergedPolygon = new google.maps.Polygon({
        fillColor: '#000',
        fillOpacity: 0.5,
        paths: mergedPolygonCoords,
        strokeOpacity: 1,
        strokeWeight: 2,
        zIndex: parseInt(zIndex) - 1,
    });
    return mergedPolygon;
};

export default (google, vertexChange, onContextMenu) => {
    const SolarModulePolygon = SolarModulePolygonBuilder(google);
    class SegmentPolygon extends google.maps.Polygon {
        onClickModule = null;
        constructor({
            drawingType,
            metadata,
            height = 0,
            id,
            onClickModule = null,
            options = {},
            safeZone = 0,
            showMeterLabels,
            solarModules = [],
            sunPosition = {},
            type = POLYGON_SEGMENT,
            zIndex = 0,
        } = {}) {
            const newOptions = {
                draggable: options?.draggable || false,
                editable: options?.editable || false,
                fillColor: options.fillColor || SEGMENT_COLOR,
                fillOpacity: options.fillOpacity || 1,
                map: options.map,
                paths: options.paths,
                strokeColor: options.strokeColor || SEGMENT_STROKE_COLOR,
                strokeOpacity: 1,
                strokePosition: google.maps.StrokePosition.OUTSIDE,
                strokeWeight: 4,
                zIndex: Math.ceil(height) + zIndex,
            };
            super(newOptions);

            this.contextSegments = null;
            this.disabled = false;
            this.drawingType = drawingType;
            this.height = parseFloat(height);
            this.id = id;
            this.isDragging = false;
            this.metadata = metadata || {};
            this.polylines = [];
            this.zonesPolygons = [];
            this.centralLine = null;

            if (onClickModule) this.onClickModule = onClickModule;

            this.safeZone = safeZone;
            this.shaderPolygon = null;
            this.sideMarkers = [];
            this.circleMarker = null;
            this.circleDiameterLine = null;
            this.circleCenter = null;
            this.circleRadius = null;
            this.sunPosition = sunPosition;
            this.type = type;
            this.viewLayers = false;
            this.onContextMenu = onContextMenu;

            if (drawingType === POLYGON_CIRCLE) {
                this.initCircleProperties();
            }

            this.meterLabels =
                drawingType === POLYGON_CIRCLE
                    ? createDiameterLabelPopup(
                          google,
                          this.map,
                          this.circleCenter,
                          this.circleRadius,
                      )
                    : createMeterLabelPopup(
                          options.paths,
                          google,
                          showMeterLabels ? this.map : null,
                      );

            this.initContextMenu();

            this.initEvents();

            this.initSetBackPolygon();

            this.initVertexChange(vertexChange);

            this.setShader();

            this.setSolarModules(solarModules);

            this.drawCentralLine();
        }

        getIsDragging() {
            return this.isDragging;
        }

        getPin() {
            const pinSvgString =
                '<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none"><circle cx="4" cy="4" r="4" fill="white"/></svg>';
            const markerContent = document.createElement('div');
            markerContent.innerHTML = `<div>${pinSvgString}</div>`;
            markerContent.style.position = 'relative';
            markerContent.style.top = '4px';
            return markerContent;
        }

        initContextMenu() {
            if (this.type !== POLYGON_SEGMENT) return;
            this.cleanContextSegments();
            this.contextSegments = buildContextSegments({
                google,
                map: this.map,
                metadata: this.metadata,
                onContextMenu: this.onContextMenu,
                segmentId: this.id,
                shapeCoords: this.getPath().getArray(),
            });
        }

        initEvents() {
            google.maps.event.addListener(this, 'drag', () => {
                if (
                    this.type === POLYGON_SEGMENT &&
                    this.drawingType === POLYGON_RECTANGLE
                ) {
                    this.initSetBackPolygon();
                    this.setSolarModulesDisabled();
                    this.resetShader();
                    this.drawZonesPolygons();
                    this.drawPolylines();
                    this.drawCentralLine();
                }
                if (this.drawingType === POLYGON_CIRCLE) {
                    this.initSetBackPolygon();
                    this.resetShader();
                }
            });
            google.maps.event.addListener(this, 'dragstart', () => {
                if (this.type === POLYGON_SEGMENT) {
                    this.setIsDragging(true);
                    if (this.drawingType === POLYGON_RECTANGLE) {
                        this.cleanSideMarkers();
                    }
                }
                if (this.drawingType === POLYGON_CIRCLE) {
                    this.setIsDragging(true);
                    this.cleanCircleMarker();
                    // Hide meter labels during drag
                    this.setLabelsVisibility(false);
                }
            });
            google.maps.event.addListener(this, 'dragend', () => {
                this.setIsDragging(false);

                if (this.type === POLYGON_SEGMENT && vertexChange) {
                    vertexChange({
                        evt: 'drag',
                        polygon: this.getPath().getArray(),
                        segmentId: this.id,
                    });
                    if (this.drawingType === POLYGON_RECTANGLE) {
                        this.setupSideMarkers(this.getPath().getArray());
                    }

                    if (this.viewLayers) {
                        this.toggleLayers(true);
                    }
                }
                if (this.drawingType === POLYGON_CIRCLE) {
                    // Recalculate center after drag
                    const coords = this.getPath().getArray();
                    this.circleCenter = this.calculateCircleCenter(coords);
                    this.setupCircleMarker();

                    // Update meter labels after moving the circle
                    this.updateMeterLabels(coords);

                    this.setLabelsVisibility(true);

                    if (vertexChange) {
                        vertexChange({
                            evt: 'drag',
                            polygon: this.getPath().getArray(),
                            segmentId: this.id,
                        });
                    }
                }
            });
        }

        initSetBackPolygon() {
            if (this.setBackPolygon) this.setBackPolygon.setMap(null);

            let tolerance = this.safeZone;

            if (this.type === POLYGON_SEGMENT) {
                tolerance *= -1;
            }

            const polygonPathsPlusTolerance = getPolygonPointsWithSetback({
                polygon: this,
                setback: tolerance,
            });

            this.setBackPolygon = new google.maps.Polygon({
                clickable: false,
                fillOpacity: 0.2,
                paths: polygonPathsPlusTolerance,
                strokeOpacity: 0,
                strokePosition: google.maps.StrokePosition.OUTSIDE,
                strokeWeight: 1,
                zIndex: Math.ceil(parseFloat(this.height)) + this.zIndex,
            });

            this.setBackPolygon.setMap(this.map);
        }

        resetShader() {
            this.setShader({
                height: this.height,
                paths: this.getPaths(),
                shaderPolygon: this.shaderPolygon,
                sunPosition: this.sunPosition,
            });
        }

        initVertexChange(vertexChange) {
            if (!vertexChange) return;

            if (this.drawingType === POLYGON_CIRCLE) return;

            addPathListeners({
                getIsDragging: () => this.getIsDragging(),
                google,
                id: this.id,
                initSetBackPolygon: () => this.initSetBackPolygon(),
                map: this.map,
                meterLabels: this.meterLabels,
                paths: this.getPaths(),
                resetShader: () => this.resetShader(),
                setContextSegments: (path) => {
                    if (this.type === POLYGON_SEGMENT) {
                        this.setContextSegments(
                            buildContextSegments({
                                google,
                                map: this.map,
                                metadata: this.metadata,
                                onContextMenu,
                                segmentId: this.id,
                                shapeCoords: path,
                            }),
                        );
                    }
                },
                setSolarModulesDisabled: () => this.setSolarModulesDisabled(),
                vertexChange,
            });
        }

        resetPosition(segmentValue, showMeterLabels) {
            const shapeCoords = segmentValue.polygon.map(
                (point) => new google.maps.LatLng(point[0], point[1]),
            );
            this.setPaths(shapeCoords);
            this.setSolarModules(segmentValue.solar_modules);
            this.setLabelsVisibility(false);
            this.meterLabels =
                this.drawingType === POLYGON_CIRCLE
                    ? createDiameterLabelPopup(
                          google,
                          this.map,
                          this.circleCenter,
                          this.circleRadius,
                      )
                    : createMeterLabelPopup(
                          shapeCoords,
                          google,
                          showMeterLabels ? this.map : null,
                      );
            this.initVertexChange(vertexChange);
        }

        setAditionalOptions(options) {
            this.setOptions(options);
        }

        setContextSegments(polylines) {
            this.cleanContextSegments();
            this.contextSegments = polylines;
        }

        cleanContextSegments() {
            if (this.contextSegments) {
                this.contextSegments.forEach((box) => {
                    google.maps.event.clearListeners(box, 'contextmenu');
                    box.setMap(null);
                });
                this.contextSegments.clear();
                this.contextSegments = null;
            }
        }

        setDisabled(isDisabled, isEditable = false) {
            if (isDisabled) {
                this.setOptions({
                    clickable: true,
                    draggable: false,
                    editable: false,
                    fillOpacity: 0.6,
                    strokeOpacity: 0.6,
                });
                this.cleanSideMarkers();
                this.cleanCircleMarker();
                this.cleanContextSegments();
            } else {
                let editable = isEditable;
                if (this.drawingType === POLYGON_RECTANGLE) {
                    editable = false;
                    this.setupSideMarkers(this.getPath().getArray());
                }
                if (this.drawingType === POLYGON_CIRCLE) {
                    editable = false;
                    this.setupCircleMarker();
                }
                this.setOptions({
                    clickable: true,
                    draggable: true,
                    editable,
                    fillOpacity: 0.6,
                    strokeOpacity: 1,
                });
                this.initContextMenu();
            }
        }

        setEnableModulesToEdit(isEnabled = true) {
            if (!Array.isArray(this.solarModules)) return;
            this.setOptions({ clickable: !isEnabled });
            this.solarModules.forEach((solarModule) => {
                solarModule.setEnableModulesToEdit(isEnabled);
            });
        }

        setHeight(height, zIndex = 0) {
            this.height = parseFloat(height);
            this.zIndex = Math.ceil(height + zIndex);
            this.setShader({ paths: this.getPaths() });
            const newZIndex = Math.ceil(this.zIndex) + 1;
            if (Array.isArray(this.solarModules)) {
                this.solarModules.forEach((solarModule) => {
                    solarModule.setNewZIndex(newZIndex);
                });
            }
        }

        setIsDragging(isDragging) {
            this.isDragging = isDragging;
        }

        setLabelsVisibility(isVisible) {
            this.meterLabels.forEach((meterLabel) =>
                meterLabel.setMap(isVisible ? this.map : null),
            );
        }

        setMap(map) {
            if (map === null) {
                if (this.meterLabels) {
                    this.meterLabels.forEach((meterLabel) =>
                        meterLabel.setMap(null),
                    );
                }
                this.meterLabels = [];
                this.getPaths().forEach((path) => {
                    google.maps.event.clearListeners(path, 'remove_at');
                    google.maps.event.clearListeners(path, 'set_at');
                    google.maps.event.clearListeners(path, 'insert_at');
                });
                google.maps.event.clearListeners(this, 'dragstart');
                google.maps.event.clearListeners(this, 'dragend');
                if (Array.isArray(this.solarModules)) {
                    this.solarModules.forEach((solarModule) => {
                        solarModule.setMap(null);
                    });
                }
                this.solarModules = [];
                if (this.shaderPolygon) this.shaderPolygon.setMap(null);
                if (this.setBackPolygon) this.setBackPolygon.setMap(null);
                this.cleanZonesPolygons();
                this.cleanCircleMarker();
                this.cleanSideMarkers();
                this.cleanCentralLine();
                this.cleanPolylines();
                this.cleanContextSegments();
            }
            super.setMap(map);
        }

        setMetadata(metadata) {
            this.metadata = metadata;
            this.drawExtraElements();
        }

        setSafeZone(safeZone) {
            this.safeZone = safeZone;
            this.initSetBackPolygon();
        }

        setSelected(isSelected, options = {}) {
            if (isSelected) {
                let editable = true;

                // For circles, disable vertex editing but enable the circle marker
                if (this.drawingType === POLYGON_CIRCLE) {
                    editable = false;
                    this.setupCircleMarker();
                }
                this.setOptions({
                    draggable: true,
                    editable: editable,
                    strokeColor: SEGMENT_STROKE_ACTIVE_COLOR,
                });
                this.meterLabels.forEach((meterLabel) =>
                    meterLabel.setMap(this.map),
                );
                // Show diameter line for circles
                if (
                    this.circleDiameterLine &&
                    this.drawingType === POLYGON_CIRCLE
                ) {
                    this.circleDiameterLine.setMap(this.map);
                }
            } else {
                this.setOptions({
                    draggable: false,
                    editable: false,
                    ...options,
                });
                this.meterLabels.forEach((meterLabel) =>
                    meterLabel.setMap(null),
                );
                // Hide diameter line
                if (this.circleDiameterLine) {
                    this.circleDiameterLine.setMap(null);
                }
            }
        }

        setShader() {
            if (this.shaderPolygon) this.shaderPolygon.setMap(null);
            if (isEmpty(this.sunPosition)) return;
            this.shaderPolygon = buildShader({
                google,
                height: this.height,
                shapeCoords: this.getPaths().getArray(),
                sunPosition: this.sunPosition,
                zIndex: this.zIndex,
            });
            if (this.shaderPolygon) this.shaderPolygon.setMap(this.map);
        }

        cleanSideMarkers() {
            if (this.sideMarkers) {
                this.sideMarkers.forEach((marker) => marker.setMap(null));
                this.sideMarkers = [];
            }
        }

        setupSideMarkers(coords) {
            try {
                this.cleanSideMarkers();

                const sides = [
                    { idx: [0, 1], directionIdx: [0, 3] },
                    { idx: [1, 2], directionIdx: [1, 0] },
                    { idx: [2, 3], directionIdx: [2, 1] },
                    { idx: [3, 0], directionIdx: [3, 2] },
                ];

                sides.forEach((side) => {
                    const p1 = coords[side.idx[0]];
                    const p2 = coords[side.idx[1]];
                    const midLat = (p1.lat() + p2.lat()) / 2;
                    const midLng = (p1.lng() + p2.lng()) / 2;
                    const marker = new google.maps.marker.AdvancedMarkerElement(
                        {
                            content: this.getPin(),
                            gmpDraggable: true,
                            map: this.map,
                            position: { lat: midLat, lng: midLng },
                        },
                    );

                    marker.sideInfo = {
                        directionIndices: side.directionIdx,
                        vertexIndices: side.idx,
                    };

                    google.maps.event.addListener(marker, 'drag', () => {
                        this.updatePolygon(marker);

                        this.setSolarModulesDisabled();
                        this.resetShader();
                        this.initSetBackPolygon();
                    });
                    google.maps.event.addListener(marker, 'dragend', () => {
                        this.updatePolygon(marker, true);
                    });
                    this.sideMarkers.push(marker);
                });
            } catch (e) {
                this.showError('Error setting up side markers: ' + e.message);
            }
        }

        setSolarModules(solarModules) {
            if (this.solarModules) {
                this.solarModules.forEach((solarModule) =>
                    solarModule.setMap(null),
                );
            }

            if (!solarModules) return;

            const newZIndex = Math.ceil(this.zIndex) + 1;

            this.solarModules = solarModules.map((solarModule) => {
                return new SolarModulePolygon({
                    cell: solarModule.cell,
                    col: solarModule.col,
                    group: solarModule.group,
                    id: solarModule.id,
                    map: this.map,
                    moduleEnable: solarModule.enabled,
                    onClick: this.onClickModule,
                    parentId: this.id,
                    paths: solarModule.path.map((modulePoint) => ({
                        lat: modulePoint[0],
                        lng: modulePoint[1],
                    })),
                    row: solarModule.row,
                    zIndex: newZIndex,
                });
            });
        }

        setSolarModulesDisabled(isDisabled = true) {
            if (!Array.isArray(this.solarModules)) return;
            this.solarModules.forEach((solarModule) => {
                solarModule.setDisabled(isDisabled);
            });
        }

        setSolarModulesFillColor(fillColor) {
            this.solarModules.forEach((solarModule) => {
                solarModule.setOptions({ fillColor });
            });
        }

        setSolarModulesStrokeColor(strokeColor) {
            this.solarModules.forEach((solarModule) => {
                solarModule.setOptions({ strokeColor });
            });
        }

        setSunPosition(sunPosition) {
            this.sunPosition = sunPosition;
            this.setShader();
        }

        showError(message) {
            console.error(message);
        }

        toggleLayers(view) {
            this.viewLayers = view;
            if (view) {
                this.drawZonesPolygons();
                this.drawPolylines();
                this.drawCentralLine();
            } else {
                this.cleanZonesPolygons();
                this.cleanPolylines();
            }
        }

        buildPolylines() {
            if (
                this.type !== POLYGON_SEGMENT ||
                this.drawingType !== POLYGON_RECTANGLE ||
                !['double_slope', 'single_slope'].includes(
                    this.metadata.sloof_type,
                )
            )
                return [];

            const forwardArrow = {
                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            };
            const backwardArrow = {
                path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
            };
            const polygonPoints = this.getPath().getArray();
            let icons = [{ icon: backwardArrow, offset: '0%' }];

            const refPoints = getRefPoints({
                google,
                polygonPoints,
                heading: this.metadata.building_heading,
            });

            if (this.metadata.sloof_type === 'double_slope') {
                icons.push({
                    icon: forwardArrow,
                    offset: '100%',
                });
            }

            const heading = parseFloat(this.metadata.building_heading);

            const point1 = computeOffsetPoint({
                google,
                start: refPoints[1],
                distance: 2,
                heading: heading + 90,
            });

            const point2 = computeOffsetPoint({
                google,
                start: refPoints[2],
                distance: 2,
                heading: heading + 90,
            });

            let points = polygonPoints.map((point) => [
                point.lng(),
                point.lat(),
            ]);
            points.push(points[0]);

            const clockwiseRing = lineString(points.map((point) => point));
            const isClockwise = booleanClockwise(clockwiseRing);

            const polyline = new google.maps.Polyline({
                path: isClockwise ? [point1, point2] : [point2, point1],
                icons,
                strokeColor: '#2AA1E0',
                strokeWeight: 3,
            });

            return [polyline];
        }

        cleanPolylines() {
            if (!Array.isArray(this.polylines) || this.polylines.length === 0)
                return;
            this.polylines.forEach((polyline) => polyline.setMap(null));
            this.polylines = [];
        }

        drawExtraElements() {
            this.drawZonesPolygons();
            this.drawCentralLine();
            this.drawPolylines();
        }

        cleanZonesPolygons() {
            if (
                !Array.isArray(this.zonesPolygons) ||
                this.zonesPolygons.length === 0
            )
                return;
            this.zonesPolygons.forEach((polygon) => polygon.setMap(null));
            this.zonesPolygons = [];
        }

        cleanCentralLine() {
            if (!this.centralLine) return;
            this.centralLine.setMap(null);
            this.centralLine = null;
        }

        drawCentralLine() {
            this.cleanCentralLine();
            if (
                this.type === POLYGON_SEGMENT &&
                this.drawingType === POLYGON_RECTANGLE &&
                this.metadata.sloof_type === 'double_slope'
            ) {
                const path = this.getPath().getArray();
                if (path.length < 2) return;
                const centralLinePoints = getCentralLine({
                    google,
                    polygonPoints: path,
                    heading: this.metadata.building_heading,
                });
                this.centralLine = new google.maps.Polyline({
                    path: centralLinePoints,
                    map: this.map,
                });
            }
        }

        drawPolylines() {
            this.cleanPolylines();
            if (this.viewLayers === false) return;
            this.polylines = this.buildPolylines();
            this.polylines.forEach((polyline) => {
                polyline.setMap(this.map);
            });
        }

        drawZonesPolygons() {
            this.cleanZonesPolygons();
            /* 
            if (
                this.viewLayers === false ||
                !['double_slope', 'single_slope'].includes(
                    this.metadata.sloof_type,
                )
            )
                return;

            const config = {
                buildingHeight: this.metadata.building_height,
                ceilTilt: this.metadata.ceil_tilt,
                google,
                heading: this.metadata.building_heading,
                map: this.map,
                polygonPoints: this.getPath().getArray(),
            };

            let zones = [];

            if (this.metadata.drawing_type === POLYGON_RECTANGLE) {
                if (this.metadata.sloof_type === 'double_slope') {
                    zones = buildPitchedRoofAreas(config);
                } else {
                    zones = buildMonoPitchedRoofAreas(config);
                }
            }

            this.zonesPolygons = zones.map(
                ({ paths, fillColor, strokeColor }) =>
                    new google.maps.Polygon({
                        fillColor,
                        fillOpacity: 0.35,
                        paths,
                        strokeColor,
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        map: this.map,
                    }),
            ); */
        }

        updatePolygon(marker, finalize = false) {
            try {
                const coords = this.getPath().getArray().slice();
                const projection = this.map.getProjection();
                if (!projection) {
                    console.warn('Map projection not ready');
                    return;
                }
                const coordsPixel = coords.map((coord) =>
                    projection.fromLatLngToPoint(coord),
                );
                const markerNewPos = marker.position;
                const markerNewPixel =
                    projection.fromLatLngToPoint(markerNewPos);

                const v1Idx = marker.sideInfo.vertexIndices[0];
                const v2Idx = marker.sideInfo.vertexIndices[1];
                const dir1Idx = marker.sideInfo.directionIndices[0];
                const dir2Idx = marker.sideInfo.directionIndices[1];

                const U = {
                    x: coordsPixel[dir2Idx].x - coordsPixel[dir1Idx].x,
                    y: coordsPixel[dir2Idx].y - coordsPixel[dir1Idx].y,
                };
                const magnitudeU = Math.sqrt(U.x * U.x + U.y * U.y);
                if (magnitudeU === 0) {
                    console.warn('Direction vector magnitude is zero');
                    return;
                }
                const U_normalized = {
                    x: U.x / magnitudeU,
                    y: U.y / magnitudeU,
                };

                const sideP1 = coordsPixel[v1Idx];
                const sideP2 = coordsPixel[v2Idx];
                const sideMidPixel = {
                    x: (sideP1.x + sideP2.x) / 2,
                    y: (sideP1.y + sideP2.y) / 2,
                };

                const V = {
                    x: markerNewPixel.x - sideMidPixel.x,
                    y: markerNewPixel.y - sideMidPixel.y,
                };

                const t = V.x * U_normalized.x + V.y * U_normalized.y;

                coordsPixel[v1Idx].x = sideP1.x + t * U_normalized.x;
                coordsPixel[v1Idx].y = sideP1.y + t * U_normalized.y;
                coordsPixel[v2Idx].x = sideP2.x + t * U_normalized.x;
                coordsPixel[v2Idx].y = sideP2.y + t * U_normalized.y;

                const newCoords = coordsPixel.map((point) =>
                    projection.fromPointToLatLng(point),
                );
                this.setPaths(newCoords);
                this.drawExtraElements();

                if (finalize) {
                    this.sideMarkers.forEach((m, i) => {
                        const side = [newCoords[i % 4], newCoords[(i + 1) % 4]];
                        const midLat = (side[0].lat() + side[1].lat()) / 2;
                        const midLng = (side[0].lng() + side[1].lng()) / 2;
                        m.position = { lat: midLat, lng: midLng };
                    });

                    if (this.type === POLYGON_SEGMENT && vertexChange) {
                        vertexChange({
                            evt: 'drag',
                            polygon: this.getPath().getArray(),
                            segmentId: this.id,
                        });
                        this.initVertexChange(vertexChange);

                        this.getPaths().forEach((path) => {
                            google.maps.event.trigger(path, 'set_at');
                        });
                    }
                }
            } catch (e) {
                this.showError('Error updating polygon: ' + e.message);
            }
        }
        // Circle methods
        initCircleProperties() {
            if (this.drawingType !== POLYGON_CIRCLE) return;

            const coords = this.getPath().getArray();
            this.circleCenter = this.calculateCircleCenter(coords);
            this.circleRadius = this.calculateCircleRadius(
                coords,
                this.circleCenter,
            );
        }

        calculateCircleCenter(coords) {
            if (!coords || coords.length === 0) return null;

            let sumLat = 0;
            let sumLng = 0;

            coords.forEach((coord) => {
                sumLat += coord.lat();
                sumLng += coord.lng();
            });

            return {
                lat: sumLat / coords.length,
                lng: sumLng / coords.length,
            };
        }

        calculateCircleRadius(coords, center) {
            if (!coords || coords.length === 0 || !center) return 0;

            const centerLatLng = new google.maps.LatLng(center.lat, center.lng);
            let totalDistance = 0;

            coords.forEach((coord) => {
                totalDistance +=
                    google.maps.geometry.spherical.computeDistanceBetween(
                        centerLatLng,
                        coord,
                    );
            });

            return totalDistance / coords.length;
        }

        generateCirclePoints(center, radius, numPoints = 64) {
            const points = [];
            const centerLatLng = new google.maps.LatLng(center.lat, center.lng);

            for (let i = 0; i < numPoints; i++) {
                const angle = (i * 360) / numPoints;
                const point = google.maps.geometry.spherical.computeOffset(
                    centerLatLng,
                    radius,
                    angle,
                );
                points.push(point);
            }

            return points;
        }

        cleanCircleMarker() {
            if (this.circleMarker) {
                google.maps.event.clearListeners(this.circleMarker, 'drag');
                google.maps.event.clearListeners(this.circleMarker, 'dragend');
                this.circleMarker.map = null;
                this.circleMarker = null;
            }
            if (this.circleDiameterLine) {
                this.circleDiameterLine.setMap(null);
                this.circleDiameterLine = null;
            }
        }

        getRadiusPin() {
            const pinSvgString =
                '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" fill="white" stroke="#002438" stroke-width="2"/></svg>';
            const markerContent = document.createElement('div');
            markerContent.innerHTML = `<div>${pinSvgString}</div>`;
            markerContent.style.position = 'relative';
            markerContent.style.top = '6px';
            markerContent.style.cursor = 'ew-resize';
            return markerContent;
        }

        setupCircleMarker() {
            try {
                this.cleanCircleMarker();

                if (!this.circleCenter || !this.circleRadius) {
                    this.initCircleProperties();
                }

                if (!this.circleCenter) return;

                // Position the marker at the right edge of the circle (east)
                const markerPosition =
                    google.maps.geometry.spherical.computeOffset(
                        new google.maps.LatLng(
                            this.circleCenter.lat,
                            this.circleCenter.lng,
                        ),
                        this.circleRadius,
                        90, // East direction
                    );

                this.circleMarker =
                    new google.maps.marker.AdvancedMarkerElement({
                        content: this.getRadiusPin(),
                        gmpDraggable: true,
                        map: this.map,
                        position: {
                            lat: markerPosition.lat(),
                            lng: markerPosition.lng(),
                        },
                    });

                google.maps.event.addListener(this.circleMarker, 'drag', () => {
                    this.updateCirclePolygon(false);
                    this.setSolarModulesDisabled();
                    this.resetShader();
                    this.initSetBackPolygon();
                });

                google.maps.event.addListener(
                    this.circleMarker,
                    'dragend',
                    () => {
                        this.updateCirclePolygon(true);
                    },
                );

                // Create diameter line
                this.updateDiameterLine();
            } catch (e) {
                this.showError('Error setting up circle marker: ' + e.message);
            }
        }

        updateDiameterLine() {
            if (!this.circleCenter || !this.circleRadius) return;

            // Calculate the two endpoints of the diameter (west to east)
            const westPoint = google.maps.geometry.spherical.computeOffset(
                new google.maps.LatLng(
                    this.circleCenter.lat,
                    this.circleCenter.lng,
                ),
                this.circleRadius,
                270, // West direction
            );

            const eastPoint = google.maps.geometry.spherical.computeOffset(
                new google.maps.LatLng(
                    this.circleCenter.lat,
                    this.circleCenter.lng,
                ),
                this.circleRadius,
                90, // East direction
            );

            if (this.circleDiameterLine) {
                // Update existing line
                this.circleDiameterLine.setPath([westPoint, eastPoint]);
            } else {
                // Create new line
                this.circleDiameterLine = new google.maps.Polyline({
                    path: [westPoint, eastPoint],
                    strokeColor: '#002438',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    map: this.map,
                    clickable: false,
                    zIndex: this.zIndex + 1,
                });
            }
        }

        updateCirclePolygon(finalize = false) {
            try {
                if (!this.circleMarker || !this.circleCenter) return;

                const markerPosition = this.circleMarker.position;
                const centerLatLng = new google.maps.LatLng(
                    this.circleCenter.lat,
                    this.circleCenter.lng,
                );

                // Calculate new radius based on marker position
                const newRadius =
                    google.maps.geometry.spherical.computeDistanceBetween(
                        centerLatLng,
                        new google.maps.LatLng(
                            markerPosition.lat,
                            markerPosition.lng,
                        ),
                    );

                // Minimum radius of 0.5 meters
                this.circleRadius = Math.max(newRadius, 0.5);

                // Generate new circle points
                const newCoords = this.generateCirclePoints(
                    this.circleCenter,
                    this.circleRadius,
                );

                this.setPaths(newCoords);

                // Update diameter line
                this.updateDiameterLine();

                if (finalize) {
                    // Update marker position to be exactly on the circle edge
                    const newMarkerPosition =
                        google.maps.geometry.spherical.computeOffset(
                            centerLatLng,
                            this.circleRadius,
                            90,
                        );

                    this.circleMarker.position = {
                        lat: newMarkerPosition.lat(),
                        lng: newMarkerPosition.lng(),
                    };

                    // Update meter labels after resizing the circle
                    this.updateMeterLabels(newCoords);

                    if (vertexChange) {
                        vertexChange({
                            evt: 'drag',
                            polygon: this.getPath().getArray(),
                            segmentId: this.id,
                        });
                    }
                }
            } catch (e) {
                this.showError('Error updating circle polygon: ' + e.message);
            }
        }

        updateMeterLabels() {
            // Remove old labels from map
            if (this.meterLabels) {
                this.meterLabels.forEach((label) => label.setMap(null));
            }

            // Create new labels with updated paths
            this.meterLabels = createDiameterLabelPopup(
                google,
                this.map,
                this.circleCenter,
                this.circleRadius,
            );
        }
    }

    return SegmentPolygon;
};
